/**
 * License-expiry parsing — single source of truth for how the frontend reads
 * the expiry values returned by `/metadata`.
 *
 * The backend now sends every license expiry as a DATE ONLY:
 *   - `licenseExpiryDt`            → `LocalDate` → "2026-10-16"
 *   - `indicatorExpiryDtMap`       → `Map<String, LocalDate>` → {"Alpha Trade": "2026-10-16"}
 *
 * That matters because `new Date("2026-10-16")` is parsed by JS as **UTC**
 * midnight, not local midnight. Feeding such a value straight into
 * `new Date(...)` comparisons is wrong in two ways:
 *
 *   1. Display shifts a day for anyone west of UTC — 2026-10-16T00:00Z is
 *      still 2026-10-15 in New York, so the user sees the wrong date.
 *   2. A license that expires TODAY reads as already expired the moment the
 *      local clock passes midnight UTC — 05:30 in India. `isUnexpired()`'s
 *      `parsed >= new Date()` would flip false at 05:31 every morning.
 *
 * So date-only values are parsed as LOCAL midnight and every comparison runs
 * at day granularity: a license stays valid for the whole of its expiry day.
 *
 * Legacy `LocalDateTime` values still held in Mongo are truncated to their
 * calendar day, which matches the semantics the backend is moving to.
 */
const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` prefix, optionally followed by a time part. */
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

/**
 * Parse a raw expiry value into a LOCAL-midnight `Date` on its calendar day.
 *
 * Returns null when the value is missing or unparseable — callers decide how
 * to treat that (the indicator engine deliberately fails OPEN so malformed
 * strings never lock paid indicators).
 */
export function parseExpiryDay(raw?: string | null): Date | null {
  if (raw === undefined || raw === null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;

  const iso = ISO_DATE_PREFIX.exec(trimmed);
  if (iso) {
    const day = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(day.getTime()) ? null : day;
  }

  // Legacy datetime (or any other parseable form): keep the calendar day only.
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

/** Local-midnight Date for today. */
export function startOfToday(): Date {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

/**
 * Whole calendar days from today until the expiry day.
 * `0` = expires today, negative = already past, null = unparseable.
 */
export function daysUntilExpiryDay(raw?: string | null): number | null {
  const day = parseExpiryDay(raw);
  if (!day) return null;
  return Math.round((day.getTime() - startOfToday().getTime()) / DAY_MS);
}

/**
 * Whether the expiry day has not yet passed — a license that expires today
 * stays active for the whole of today.
 *
 * Returns null when the value is unparseable so callers can preserve their
 * own lenient fallback.
 */
export function isExpiryDayActive(raw?: string | null): boolean | null {
  const diff = daysUntilExpiryDay(raw);
  if (diff === null) return null;
  return diff >= 0;
}
